import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import { useCreateProgramVersion } from '../../../api/hooks/useProgramVersions';
import { useOrganization } from '../../../contexts/OrganizationContext';

interface CreateVersionModalProps {
  open: boolean;
  onClose: () => void;
  programId: string;
  existingVersions?: any[];
}

const CreateVersionModal: React.FC<CreateVersionModalProps> = ({
  open,
  onClose,
  programId,

}) => {
  const [id, setId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [website, setWebsite] = useState('');
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateProgramVersion(
    selectedOrganization?.id || '',
    programId
  );

  useEffect(() => {
    if (open) {
      setId(uuidv4()); // Generate UUID when modal opens
    } else {
      setId(null); // Clean UUID when modal closes
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }

    try {
      await createMutation.mutateAsync({
        ...(id && { id }), // Include UUID in DTO
        name: name.trim(),
        description: description.trim() || "",
        programId,
        documentUrl: documentUrl.trim() || "",
        website: website.trim() || "",
        isActive: true,
      });

      enqueueSnackbar('Versión creada exitosamente', { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar('Error al crear la versión', { variant: 'error' });
      console.error('Error creating version:', error);
    }
  };

  const handleClose = () => {
    setId(null); // Clean UUID on close
    setName('');
    setDescription('');
    setDocumentUrl('');
    setWebsite('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crear Nueva Versión</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="URL del Documento (opcional)"
            value={documentUrl}
            onChange={(e) => setDocumentUrl(e.target.value)}
            fullWidth
            placeholder="https://..."
          />
          <TextField
            label="Sitio Web (opcional)"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            fullWidth
            placeholder="https://..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createMutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createMutation.isPending}
          startIcon={createMutation.isPending && <CircularProgress size={20} />}
        >
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateVersionModal;

